import React, { useEffect, useRef } from 'react';

import { useMultiMacroQueryStoreHook } from '@confluence/fabric-extension-queries/entry-points/multiMacroQuery.ts';
import { queryStaticMacro } from '@confluence/fabric-extension-queries/entry-points/singleStaticMacroQuery.ts';
import { fg } from '@confluence/feature-gating';
import { getLogger } from '@confluence/logger';

import {
	StaticMacroStoreProvider,
	type StaticMacroAttempt,
	useStaticMacroStoreContext,
} from './StaticMacroStoreContext.tsx';

const logger = getLogger('StaticMacroDataBridge');

/**
 * This loads the multi-macro query store into the static macro store context.
 *
 * Sits in `MultiMacroQueryContainer` and `StaticMacroStoreProvider`, reads `staticMacroData` from the query store
 * and registers entries into the static macro context. Each `ForgeMacro` is only notified when its own data arrives.
 * When all staticMacros pages have been fetched, calls `markFullyLoaded` so pending macros know to fall back to the iframe renderer.
 *
 * Also wires up `ctx.triggerRefetch` (via `setRefetchTrigger`) to the single-macro
 * `confluence_staticMacro` query, so an individual `ForgeMacro` instance can request fresh static
 * HTML for itself without re-fetching/re-paginating every macro on the page.
 */
export function StaticMacroDataBridge({
	draftSyncCompletedRef,
}: {
	draftSyncCompletedRef?: React.MutableRefObject<(() => void) | null>;
}): null {
	const ctx = useStaticMacroStoreContext();
	const [{ staticMacroData, staticMacrosComplete, contentId, cloudId, mode }] =
		useMultiMacroQueryStoreHook();

	// List of all currently registered localIds so we don't do duplicate work if we received a localId multiple times.
	const registeredLocalIds = useRef<Set<string>>(new Set());
	const prevStaticMacroPagesRef = useRef<Record<string, any> | null>(null);
	const fullyLoadedRef = useRef(false);

	useEffect(() => {
		if (!ctx || !draftSyncCompletedRef) {
			return undefined;
		}
		draftSyncCompletedRef.current = ctx.notifyDraftSyncCompleted;
		return () => {
			draftSyncCompletedRef.current = null;
		};
	}, [ctx, draftSyncCompletedRef]);

	useEffect(() => {
		if (!ctx || !contentId) {
			return undefined;
		}

		ctx.setRefetchTrigger((localId: string, attempt: StaticMacroAttempt) => {
			const fetchStaticMacro = (hasRetried: boolean) => {
				void queryStaticMacro({ contentId, cloudId, localId, mode })
					.then((result) => {
						if (!result) return;
						if (result.value) {
							ctx.register(localId, result.value, { force: true, attempt });
						} else if (result.errorCode) {
							ctx.registerError(localId, result.errorCode, { force: true, attempt });
						}
					})
					.catch((error) => {
						if (!hasRetried) {
							fetchStaticMacro(true);
							return;
						}
						logger.error`Static macro refetch failed after retry for localId ${localId} on content ${contentId}: ${error}`;
						ctx.registerError(localId, 'REFETCH_FAILED', { force: true, attempt });
					});
			};

			fetchStaticMacro(false);
		});

		return () => ctx.setRefetchTrigger(null);
	}, [ctx, contentId, cloudId, mode]);

	useEffect(() => {
		if (!ctx) return;

		if (staticMacroData !== prevStaticMacroPagesRef.current) {
			prevStaticMacroPagesRef.current = staticMacroData;

			for (const node of Object.values(staticMacroData ?? {})) {
				const { localId, value, error } = node;
				if (!localId || registeredLocalIds.current.has(localId)) continue;
				registeredLocalIds.current.add(localId);

				if (value) {
					ctx.register(localId, value);
				} else if (error) {
					ctx.registerError(localId, error.code);
				}
			}
		}

		if (staticMacrosComplete && !fullyLoadedRef.current) {
			fullyLoadedRef.current = true;
			ctx.markFullyLoaded();
		}
	}, [ctx, staticMacroData, staticMacrosComplete]);

	return null;
}

export function StaticMacrosBridge({
	children = null,
	draftSyncCompletedRef,
}: {
	children?: React.ReactNode;
	draftSyncCompletedRef?: React.MutableRefObject<(() => void) | null>;
}): React.ReactNode {
	// Always render the provider so the tree shape is stable between SSR and the first client render.
	return (
		<StaticMacroStoreProvider>
			{fg('conf_frontend_forge_static_macro_mvp') && (
				<StaticMacroDataBridge draftSyncCompletedRef={draftSyncCompletedRef} />
			)}
			{children}
		</StaticMacroStoreProvider>
	);
}
